import { useState } from "react";
import { Button, Input, Alert } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

const DEFAULT_SAMPLE_TRANSCRIPT = `Provider: Tell me what brought you in today.

Patient: I've been struggling with my weight for years. I'm here for help with weight management. I have type 2 diabetes and high blood pressure.

Provider: Got it. Let me look at your numbers...`;

/**
 * Right pane: on-demand preview of the rendered note against a sample transcript.
 *
 * The preview is generated only when the admin clicks "Generate preview" (it runs
 * the real note-generation pipeline, which can take up to ~90 seconds). There is
 * no auto-refresh and no spinner; while a preview is generating the button is
 * disabled and shows a plain status line.
 *
 * Backend endpoint: POST /api/v1/templates/{id}/preview or
 * POST /api/v1/templates/preview-draft for unsaved definitions.
 */
export default function TemplatePreview({
  previewState,
  pendingChanges,
  onRefreshPreview,
}) {
  const [transcript, setTranscript] = useState(DEFAULT_SAMPLE_TRANSCRIPT);
  const loading = !!previewState?.loading;

  return (
    <section className="w-1/3 border-l border-gray-200 bg-white flex flex-col">
      <header className="p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-sm text-gray-700">Preview</h3>
        <Button
          size="small"
          type="primary"
          icon={<ReloadOutlined />}
          onClick={() => onRefreshPreview?.(transcript)}
          disabled={loading}
        >
          {loading ? "Generating…" : "Generate preview"}
        </Button>
      </header>

      <div className="p-3 border-b border-gray-100">
        <label className="block text-xs text-gray-500 mb-1">Sample transcript</label>
        <Input.TextArea
          rows={4}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          disabled={loading}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {pendingChanges && (
          <Alert
            type="info"
            showIcon
            message="Showing draft preview"
            description="Template has unsaved changes. Save to persist this version."
            className="mb-3"
          />
        )}

        {loading && (
          <p className="text-xs text-gray-500 py-8 text-center">
            Generating preview against the sample transcript. This can take up to ~90 seconds.
          </p>
        )}

        {!loading && previewState?.error && (
          <Alert type="error" showIcon message={previewState.error} />
        )}

        {!loading && !previewState?.error && previewState?.note && (
          <PreviewNote note={previewState.note} />
        )}

        {!loading && !previewState?.error && !previewState?.note && (
          <p className="text-gray-400 text-sm">
            No preview yet. Click <b>Generate preview</b> to render the note against the sample transcript.
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Renders the per-section AMD fields the LLM populated. The shape mirrors
 * what the generation pipeline writes today: a dict keyed by amd_field_name
 * with text values. Per-section labels and the AI-off badge match what the
 * clinician will see.
 */
function PreviewNote({ note }) {
  if (!note || typeof note !== "object") {
    return <pre className="text-xs whitespace-pre-wrap">{String(note)}</pre>;
  }
  return (
    <div className="space-y-3">
      {Object.entries(note).map(([fieldName, value]) => (
        <div key={fieldName} className="border-l-2 border-blue-300 pl-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{fieldName}</h4>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{value || <em className="text-gray-400">(empty)</em>}</p>
        </div>
      ))}
    </div>
  );
}
